import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import type { AdminRoleCode } from '@store/slices/auth';
import { getSidebarNav } from './nav.config';

interface Props {
  roles: readonly AdminRoleCode[];
  collapsed: boolean;
  /** Fired after a nav item is activated. Used by the mobile drawer to close. */
  onItemActivate?: () => void;
}

export function SidebarNav({ roles, collapsed, onItemActivate }: Props) {
  const sections = useMemo(() => getSidebarNav(roles), [roles]);

  return (
    <nav className="flex-1 overflow-y-auto px-2 py-2">
      {sections.map((section) => (
        <div key={section.label} className="mb-4">
          {!collapsed && (
            <div className="mb-1 px-3 text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-muted">
              {section.label}
            </div>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    title={collapsed ? item.label : undefined}
                    onClick={onItemActivate}
                    className={({ isActive }) =>
                      [
                        'group flex h-10 items-center rounded-md text-[13.5px] font-medium transition-colors',
                        'border-l-2',
                        isActive
                          ? 'border-primary bg-primary/[0.08] text-primary'
                          : 'border-transparent text-foreground-muted hover:bg-primary/[0.04] hover:text-foreground',
                        collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                      ].join(' ')
                    }
                  >
                    <Icon size={20} className="shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
